'use client'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { SongArtwork } from '@/components/ui/song-artwork'
import {
  InlineDialog,
  InlineDialogHeader,
  InlineDialogFooter,
  InlineDialogTitle,
  InlineDialogDescription,
} from './inline-dialog'
import {
  Music,
  Play,
  ThumbsUp,
  MessageSquare,
  DollarSign,
  X,
  ChevronDown,
  Ban,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import type { DemoQueueRequest } from '../demo-types'

// Queue request item component - matches production with better mobile layout
function QueueRequestItem({
  request,
  index,
  onSkip,
  onPlay,
  dataCoach,
}: {
  request: DemoQueueRequest
  index: number
  onSkip: () => void
  onPlay: () => void
  dataCoach?: string
}) {
  const t = useTranslations('demo')
  return (
    <div {...(dataCoach ? { 'data-coach': dataCoach } : {})} className="p-3 rounded-lg border bg-zinc-900/50 border-zinc-800">
      {/* Top row: index, artwork, song info */}
      <div className="flex items-center gap-3">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center font-bold text-sm shrink-0 bg-amber-500/20 text-amber-400">
          {index + 1}
        </div>
        <SongArtwork src={request.song?.artworkUrl} size={44} className="h-11 w-11 rounded-md shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-white truncate">{request.song?.title}</p>
          <p className="text-sm text-zinc-400 truncate">{request.song?.artist}</p>
        </div>
      </div>

      {/* Bottom row: badges on left, buttons on right */}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-zinc-800/50">
        <div className="flex items-center gap-1.5">
          {request.totalTipsCents > 0 && (
            <Badge className="bg-green-500/20 text-green-400 text-xs px-1.5">
              <DollarSign className="h-3 w-3 mr-0.5" />
              ${(request.totalTipsCents / 100).toFixed(0)}
            </Badge>
          )}
          {request.upvotes > 0 && (
            <Badge className="bg-violet-500/20 text-violet-400 text-xs px-1.5">
              <ThumbsUp className="h-3 w-3 mr-0.5" />
              {request.upvotes}
            </Badge>
          )}
        </div>
        <div className="flex gap-1.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={onSkip}
                className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10 h-8 w-8 p-0"
              >
                <Ban className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="bg-zinc-800 border-zinc-700 text-zinc-200">{t('skipAction')}</TooltipContent>
          </Tooltip>
          <Button size="sm" onClick={onPlay} className="bg-green-600 hover:bg-green-700 h-8 w-8 p-0">
            <Play className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

export interface QueueManagementProps {
  queue: DemoQueueRequest[]
  confirmSkipRequest: DemoQueueRequest | null
  isAllRequestsOpen: boolean
  onSetConfirmSkipRequest: (request: DemoQueueRequest | null) => void
  onSetAllRequestsOpen: (open: boolean) => void
  onPlayFromQueue: (request: DemoQueueRequest) => void
  onConfirmSkip: () => void
}

export function QueueManagement({
  queue,
  confirmSkipRequest,
  isAllRequestsOpen,
  onSetConfirmSkipRequest,
  onSetAllRequestsOpen,
  onPlayFromQueue,
  onConfirmSkip,
}: QueueManagementProps) {
  const t = useTranslations('demo')
  return (
    <>
      {/* Request Queue Card */}
      <Card className={`border-zinc-800 ${queue.length > 0 ? 'border-amber-500/30 bg-gradient-to-r from-amber-900/20 to-orange-900/20' : 'bg-zinc-900/50'}`}>
        <CardHeader className="pb-2 px-4">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <MessageSquare className={`h-4 w-4 ${queue.length > 0 ? 'text-amber-400' : 'text-zinc-400'}`} />
            {t('audienceRequests')}
            {queue.length > 0 && (
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">{queue.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {queue.length > 0 ? (
            <div className="space-y-2">
              {queue.slice(0, 5).map((request, index) => (
                <QueueRequestItem
                  key={request.id}
                  request={request}
                  index={index}
                  onSkip={() => onSetConfirmSkipRequest(request)}
                  onPlay={() => onPlayFromQueue(request)}
                  {...(index === 0 ? { dataCoach: 'play-queue' } : {})}
                />
              ))}
              {queue.length > 5 && (
                <Button
                  variant="outline"
                  className="w-full border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                  onClick={() => onSetAllRequestsOpen(true)}
                >
                  <ChevronDown className="h-4 w-4 mr-2" />
                  {queue.length - 5 !== 1 ? t('viewMoreArtistRequestsPlural', { count: queue.length - 5 }) : t('viewMoreArtistRequests', { count: queue.length - 5 })}
                </Button>
              )}
            </div>
          ) : (
            <div className="text-center py-6 text-zinc-400">
              <Music className="h-8 w-8 mx-auto mb-2 text-zinc-600" />
              <p className="text-sm">{t('noSongRequestsYet')}</p>
              <p className="text-xs text-zinc-500 mt-1">{t('requestsFromAudience')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Skip Request Confirmation */}
      <InlineDialog open={!!confirmSkipRequest} onOpenChange={(open) => !open && onSetConfirmSkipRequest(null)}>
        <InlineDialogHeader onClose={() => onSetConfirmSkipRequest(null)}>
          <InlineDialogTitle className="flex items-center gap-2">
            <Ban className="h-5 w-5 text-red-400" />
            {t('skipSongTitle', { songTitle: confirmSkipRequest?.song.title ?? '' })}
          </InlineDialogTitle>
          <InlineDialogDescription>
            {t('removeFromQueue')}
            {confirmSkipRequest && confirmSkipRequest.totalTipsCents > 0 && (
              <span className="block mt-2 text-amber-400">
                {t('warningBoostAttached', { amount: (confirmSkipRequest.totalTipsCents / 100).toFixed(2) })}
              </span>
            )}
          </InlineDialogDescription>
        </InlineDialogHeader>
        <InlineDialogFooter>
          <Button variant="outline" onClick={() => onSetConfirmSkipRequest(null)} className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">{t('keepRequest')}</Button>
          <Button onClick={onConfirmSkip} className="bg-red-600 hover:bg-red-700 text-white">{t('skipRequest')}</Button>
        </InlineDialogFooter>
      </InlineDialog>

      {/* All Requests Overlay - contained within artist viewport */}
      {isAllRequestsOpen && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-lg max-h-[85%] shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-zinc-800 shrink-0">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-amber-400" />
                {t('allSongRequests')}
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">{queue.length}</Badge>
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSetAllRequestsOpen(false)}
                className="h-8 w-8 p-0 text-zinc-400 hover:text-white"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {queue.length > 0 ? (
                <div className="space-y-2">
                  {queue.map((request, index) => (
                    <QueueRequestItem
                      key={request.id}
                      request={request}
                      index={index}
                      onSkip={() => { onSetConfirmSkipRequest(request); onSetAllRequestsOpen(false) }}
                      onPlay={() => { onPlayFromQueue(request); onSetAllRequestsOpen(false) }}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-zinc-400">
                  <Music className="h-10 w-10 mx-auto mb-2 text-zinc-600" />
                  <p className="text-sm">{t('noSongRequests')}</p>
                </div>
              )}
            </div>
            <div className="border-t border-zinc-800 p-4 shrink-0">
              <Button
                variant="outline"
                onClick={() => onSetAllRequestsOpen(false)}
                className="w-full border-zinc-700"
              >
                {t('close')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
